import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FileSearch, RefreshCw, Download, AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';

interface Props {
  projectId: string;
  snapshotId: string;
  onClose: () => void;
}

interface TriageReport {
  ranAt: string;
  total: number;
  criticals: number;
  highs: number;
  mediums: number;
  lows: number;
  recommendations: string[];
  risks: { sev: 'high' | 'medium'; text: string }[];
}

interface RawTriage {
  summary?: { total?: number; critical?: number; high?: number; medium?: number; low?: number };
  recommendations?: string[];
  risks?: { sev?: string; text?: string }[];
}

const SEV_COLOR = { high: '#F59E0B', medium: '#6B7280' };

/** Triage Modal: POST /triage с localStorage кэшем и фейковым прогресс-баром 8 сек. */
export function TriageModal({ projectId, snapshotId, onClose }: Props) {
  const cacheKey = `triage_${projectId}_${snapshotId}`;
  const [phase, setPhase] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [report, setReport] = useState<TriageReport | null>(() => {
    try {
      const c = localStorage.getItem(cacheKey);
      return c ? JSON.parse(c) as TriageReport : null;
    } catch { return null; }
  });
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (report) setPhase('done');
  }, [report]);

  const run = useMutation({
    mutationFn: () => api.post<RawTriage>(`/projects/${projectId}/snapshots/${snapshotId}/triage?curator=curator&report_format=json`, {}),
    onMutate: () => {
      setPhase('running');
      setProgress(0);
    },
    onSuccess: (raw) => {
      const result: TriageReport = {
        ranAt: new Date().toLocaleString('ru-RU'),
        total:     raw.summary?.total    || 0,
        criticals: raw.summary?.critical || 0,
        highs:     raw.summary?.high     || 0,
        mediums:   raw.summary?.medium   || 0,
        lows:      raw.summary?.low      || 0,
        recommendations: raw.recommendations || ['Сортировка выполнена — рекомендаций нет'],
        risks: (raw.risks || []).map((r) => ({
          sev: (r.sev === 'high' ? 'high' : 'medium') as 'high' | 'medium',
          text: r.text || '',
        })),
      };
      setReport(result);
      try { localStorage.setItem(cacheKey, JSON.stringify(result)); } catch { /* swallow */ }
      setPhase('done');
      toast.success('Сортировка завершена');
    },
    onError: (e) => {
      setPhase('error');
      toast.error(e instanceof Error ? e.message : 'Сортировка не выполнена');
    },
  });

  // Фейковый прогресс пока бекенд выполняет реальный запрос
  useEffect(() => {
    if (phase !== 'running') return;
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(95, Math.round((elapsed / 8000) * 100));
      setProgress(p);
    }, 200);
    return () => clearInterval(id);
  }, [phase]);

  const footer = (
    <>
      <span className="text-muted mr-auto" style={{ fontSize: 11 }}>
        {report ? 'Кэш сохранён в localStorage' : ''}
      </span>
      {report && (
        <Button variant="secondary" size="sm" icon={<Download size={14} />} onClick={() => exportCsv(projectId, snapshotId, report)}>
          Экспорт CSV
        </Button>
      )}
      <Button variant="primary" onClick={onClose} disabled={phase === 'running'}>Закрыть</Button>
    </>
  );

  return (
    <Modal title="Сортировка замечаний" onClose={onClose} maxWidth={720} footer={footer}>
      <p className="text-muted" style={{ fontSize: 12 }}>Анализ всех замечаний по проекту с рекомендациями. Занимает до 90 сек.</p>

      {phase === 'idle' && !report && (
        <div className="text-center" style={{ padding: '32px 0' }}>
          <FileSearch size={56} stroke="#6B7280" style={{ margin: '0 auto' }} />
          <p className="text-ink mt-4" style={{ fontSize: 14 }}>Сортировка замечаний ещё не запускалась</p>
          <Button variant="primary" className="mt-4" onClick={() => run.mutate()}>Запустить сортировку</Button>
        </div>
      )}

      {phase === 'running' && (
        <div className="text-center" style={{ padding: '32px 0' }}>
          <span className="rounded-pill tabular inline-block" style={{ padding: '6px 16px', background: 'rgba(245,158,11,.1)', color: '#B45309', fontSize: 12, fontWeight: 600 }}>
            Анализирую — {progress}%
          </span>
          <div className="mt-5 rounded-pill overflow-hidden" style={{ height: 6, background: '#F3F4F6' }}>
            <div style={{ height: '100%', width: progress + '%', background: '#F59E0B', transition: 'width .25s' }} />
          </div>
          <p className="text-muted mt-4" style={{ fontSize: 13 }}>
            Не закрывайте окно — обработка занимает до 90 сек для крупных проектов.
          </p>
        </div>
      )}

      {phase === 'done' && report && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-muted tabular" style={{ fontSize: 12 }}>Сортировка от {report.ranAt}</span>
            <Button variant="secondary" size="sm" icon={<RefreshCw size={12} />} onClick={() => run.mutate()}>Запустить заново</Button>
          </div>
          <div className="grid grid-cols-4 mb-3" style={{ gap: 12 }}>
            {[
              { k: 'Всего',         v: report.total,                  c: '#0A1628',
                tip: 'Все открытые замечания в текущей редакции' },
              { k: 'Критические',   v: report.criticals,               c: '#EF4444',
                tip: 'Дефекты проектного решения, влияющие на безопасность объекта. Блокируют положительное заключение экспертизы.' },
              { k: 'Высокие',       v: report.highs,                   c: '#F59E0B',
                tip: 'Пробелы комплектности и несоответствие требованиям к составу ПД. Требуют ответа проектировщика.' },
              { k: 'Средние/низкие', v: report.mediums + report.lows, c: '#6B7280',
                tip: 'Прочие недостатки и уточнения. Не блокируют заключение, но требуют устранения.' },
            ].map((m, i) => (
              <div key={i} className="rounded-card bg-surface border border-border" title={m.tip} style={{ padding: 14, cursor: 'help' }}>
                <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>{m.k}</div>
                <div className="font-heading tabular mt-1" style={{ fontSize: 24, fontWeight: 600, color: m.c }}>{m.v}</div>
              </div>
            ))}
          </div>
          <div className="text-muted mb-5" style={{ fontSize: 11, lineHeight: 1.5, padding: '0 2px' }}>
            <b style={{ color: '#EF4444' }}>Критические</b> — дефекты проектного решения, влияющие на безопасность объекта (блокируют заключение).
            {' '}<b style={{ color: '#F59E0B' }}>Высокие</b> — пробелы комплектности и несоответствие требованиям к составу ПД.
            {' '}<b style={{ color: '#6B7280' }}>Средние/низкие</b> — прочие недостатки и уточнения.
            <br/>
            Классификация определяется по подзаголовку категории замечания в исходном файле (например, «Недостатки, оказывающие влияние на безопасность» → критическое).
          </div>
          <div className="mb-5">
            <h4 className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Рекомендации</h4>
            <ul className="mt-2" style={{ paddingLeft: 18 }}>
              {report.recommendations.map((r, i) => <li key={i} className="text-ink" style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 4 }}>{r}</li>)}
            </ul>
          </div>
          {report.risks.length > 0 && (
            <div>
              <h4 className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Риски</h4>
              <div className="mt-2">
                {report.risks.map((r, i) => (
                  <div key={i} className="flex items-start" style={{ padding: '8px 0', gap: 10 }}>
                    <AlertTriangle size={14} stroke={SEV_COLOR[r.sev]} />
                    <span className="text-ink" style={{ fontSize: 13 }}>{r.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {phase === 'error' && (
        <div className="rounded-card mt-4" style={{ padding: 16, background: 'rgba(239,68,68,.05)', border: '1px solid rgba(239,68,68,.2)' }}>
          <p className="text-ink" style={{ fontSize: 13 }}>Не удалось выполнить сортировку. Backend может быть недоступен — попробуйте позже.</p>
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => run.mutate()}>Повторить</Button>
        </div>
      )}
    </Modal>
  );
}

// ─── CSV-экспорт реальный (а не toast-заглушка) ─────────────────────────────
interface RemarkRow {
  id: string;
  severity: string | null;
  section_code: string | null;
  num_in_section: number | null;
  body: string;
  defect_type: string | null;
  closed_at: string | null;
  specialist_role: string | null;
}

async function exportCsv(projectId: string, snapshotId: string, report: TriageReport): Promise<void> {
  try {
    const resp = await api.get<{ remarks: RemarkRow[] }>(
      `/projects/${projectId}/remarks?snapshot_id=${snapshotId}`,
    );
    const rows = resp.remarks || [];
    const sevRu: Record<string, string> = {
      critical: 'Критическое', Critical: 'Критическое',
      high: 'Высокое',          High: 'Высокое',
      medium: 'Среднее',        Medium: 'Среднее',
      low: 'Низкое',            Low: 'Низкое',
    };
    const typeRu: Record<string, string> = {
      'S-defect': 'Блокирующее',
      'E-gap':    'Устранимое',
    };
    const header = ['№', 'Раздел', 'Серьёзность', 'Тип дефекта', 'Статус', 'Замечание'];
    const lines: string[] = [header.map(csvField).join(';')];
    for (const r of rows) {
      const num = r.num_in_section ?? '';
      const sec = r.section_code ?? '';
      const sev = sevRu[r.severity ?? ''] ?? (r.severity ?? '—');
      const typ = typeRu[r.defect_type ?? ''] ?? (r.defect_type ?? '');
      const status = r.closed_at ? 'Закрыто' : 'Открыто';
      const body = (r.body ?? '').replace(/\r?\n/g, ' ').trim();
      lines.push([num, sec, sev, typ, status, body].map(csvField).join(';'));
    }
    // BOM нужен чтобы Excel правильно открыл UTF-8.
    const blob = new Blob(['﻿' + lines.join('\r\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const ts = report.ranAt.replace(/[^\d]/g, '_').slice(0, 19);
    const filename = `Сортировка_замечаний_${ts}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
    toast.success(`Файл «${filename}» скачан (${rows.length} замечаний)`);
  } catch (e) {
    toast.error(`Не удалось скачать CSV: ${(e as Error)?.message ?? 'unknown'}`);
  }
}

function csvField(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  // Excel: экранирование " через "" и обрамление кавычками если есть спецсимволы.
  if (/[";\r\n]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}
